import {
    collection,
    doc,
    getCountFromServer,
    getDoc,
    getDocs,
    serverTimestamp,
    setDoc,
    updateDoc,
} from "firebase/firestore";
import { db } from "../firebase/config";
import {
    DEFAULT_ORGANISATION_CONFIG,
    Organisation,
    OrganisationConfig,
    OrganisationMember,
    OrganisationMemberNote,
    OrganisationSubcollectionEntry,
    OrganisationSubcollectionName,
    OrganisationWithCounts,
    OwnerDocIssue,
    resolveAthleteSubscription,
} from "../types/organisation";

const toDate = (value: unknown): Date | undefined => {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    if (typeof (value as { toDate?: unknown }).toDate === "function") {
        return (value as { toDate: () => Date }).toDate();
    }
    return undefined;
};

const mapRole = (value: unknown): OrganisationMember["role"] => {
    if (value === "owner" || value === "admin" || value === "coach" || value === "member" || value === "medical" || value === "athlete") {
        return value;
    }
    return "member";
};

const mapStatus = (value: unknown): OrganisationMember["status"] => {
    if (value === "active" || value === "pending" || value === "suspended") {
        return value;
    }
    return "active";
};

const mapOrganisationMember = (orgId: string, memberId: string, data: Record<string, unknown>): OrganisationMember => ({
    userId: memberId,
    orgId,
    role: mapRole(data.role),
    joinedAt: toDate(data.joinedAt),
    invitedBy: typeof data.invitedBy === "string" ? data.invitedBy : undefined,
    status: mapStatus(data.status),
    team: typeof data.team === "string" ? data.team : null,
    teams: Array.isArray(data.teams) ? data.teams.filter((value): value is string => typeof value === "string") : undefined,
    groups: Array.isArray(data.groups) ? data.groups.filter((value): value is string => typeof value === "string") : undefined,
    profile: data.profile,
    privacySettings: (data.privacySettings && typeof data.privacySettings === "object")
        ? (data.privacySettings as OrganisationMember["privacySettings"])
        : undefined,
});

export const createOrganisation = async (name: string): Promise<string> => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
    const randomId = Array.from({ length: 22 }, () => chars[Math.floor(Math.random() * chars.length)]).join("");
    const orgId = `org_${randomId}`;

    await setDoc(doc(db, "organisations", orgId), {
        name: name.trim(),
        createdAt: serverTimestamp(),
        lastUpdated: serverTimestamp(),
    });

    return orgId;
};

export const fetchOrganisationById = async (orgId: string): Promise<Organisation | null> => {
    const orgDoc = await getDoc(doc(db, "organisations", orgId));
    if (!orgDoc.exists()) return null;

    const orgData = orgDoc.data() as Record<string, unknown>;
    const membersSnapshot = await getDocs(collection(db, "organisations", orgId, "members"));
    const members = membersSnapshot.docs.map((memberDoc) => mapOrganisationMember(orgId, memberDoc.id, memberDoc.data() as Record<string, unknown>));

    return {
        id: orgId,
        name: typeof orgData.name === "string" ? orgData.name : "Unknown Organisation",
        createdAt: toDate(orgData.createdAt),
        updatedAt: toDate(orgData.updatedAt),
        lastUpdated: toDate(orgData.lastUpdated),
        ownerId: typeof orgData.ownerId === "string" ? orgData.ownerId : undefined,
        inviteCode: typeof orgData.inviteCode === "string" ? orgData.inviteCode : undefined,
        onboardingComplete: typeof orgData.onboardingComplete === "boolean" ? orgData.onboardingComplete : undefined,
        adminActivityCatIDs: Array.isArray(orgData.adminActivityCatIDs)
            ? orgData.adminActivityCatIDs.filter((value): value is string => typeof value === "string")
            : undefined,
        logoUrl: typeof orgData.logoUrl === "string" ? orgData.logoUrl : undefined,
        imageUrl: typeof orgData.imageUrl === "string" ? orgData.imageUrl : undefined,
        description: typeof orgData.description === "string" ? orgData.description : undefined,
        teams: Array.isArray(orgData.teams) ? orgData.teams.filter((value): value is string => typeof value === "string") : undefined,
        groups: orgData.groups && typeof orgData.groups === "object"
            ? (orgData.groups as Record<string, string[]>)
            : undefined,
        members,
    };
};

export const getOwnerDocIssue = async (orgId: string, ownerId: string | undefined): Promise<OwnerDocIssue | null> => {
    if (!ownerId) return null;
    const ownerDoc = await getDoc(doc(db, "organisations", orgId, "owners", ownerId));
    if (!ownerDoc.exists()) return "missing";
    const status = ownerDoc.data().status;
    return status === undefined || status === "active" ? null : "inactive";
};

export const fetchOrganisationsWithCounts = async (): Promise<OrganisationWithCounts[]> => {
    const orgsSnapshot = await getDocs(collection(db, "organisations"));

    return Promise.all(
        orgsSnapshot.docs.map(async (orgDoc) => {
            const orgId = orgDoc.id;
            const orgData = orgDoc.data() as Record<string, unknown>;

            const ownerId = typeof orgData.ownerId === "string" ? orgData.ownerId : undefined;

            const [ownerSnap, coachSnap, athleteSnap, configSnap, ownerDocIssue] = await Promise.all([
                getCountFromServer(collection(db, "organisations", orgId, "owners")),
                getCountFromServer(collection(db, "organisations", orgId, "coaches")),
                getCountFromServer(collection(db, "organisations", orgId, "members")),
                getDoc(doc(db, "organisations", orgId, "config", "settings")),
                getOwnerDocIssue(orgId, ownerId),
            ]);

            let config: OrganisationConfig | undefined;
            if (configSnap.exists()) {
                const configData = configSnap.data() as Record<string, unknown>;
                config = {
                    maxAthletes: typeof configData.maxAthletes === "number" ? configData.maxAthletes : 0,
                    maxAthletesForPas: typeof configData.maxAthletesForPas === "number" ? configData.maxAthletesForPas : 0,
                    maxCoaches: typeof configData.maxCoaches === "number" ? configData.maxCoaches : 0,
                    maxTeams: typeof configData.maxTeams === "number" ? configData.maxTeams : 0,
                    billingPeriod: configData.billingPeriod === "quarterly" ? "quarterly" : "annually",
                    onboardingRequested: configData.onboardingRequested === true,
                    athleteSubscription: resolveAthleteSubscription(configData.athleteSubscription),
                    orgType: configData.orgType === "team" || configData.orgType === "commercial" ? configData.orgType : "individual",
                    status: configData.status === "trial" || configData.status === "active" || configData.status === "pastDue" || configData.status === "paused" || configData.status === "canceled"
                        ? configData.status
                        : undefined,
                };
            }

            return {
                id: orgId,
                name: typeof orgData.name === "string" ? orgData.name : "Unknown Organisation",
                createdAt: toDate(orgData.createdAt),
                updatedAt: toDate(orgData.updatedAt),
                lastUpdated: toDate(orgData.lastUpdated),
                ownerId,
                inviteCode: typeof orgData.inviteCode === "string" ? orgData.inviteCode : undefined,
                onboardingComplete: typeof orgData.onboardingComplete === "boolean" ? orgData.onboardingComplete : undefined,
                adminActivityCatIDs: Array.isArray(orgData.adminActivityCatIDs)
                    ? orgData.adminActivityCatIDs.filter((value): value is string => typeof value === "string")
                    : undefined,
                logoUrl: typeof orgData.logoUrl === "string" ? orgData.logoUrl : undefined,
                imageUrl: typeof orgData.imageUrl === "string" ? orgData.imageUrl : undefined,
                description: typeof orgData.description === "string" ? orgData.description : undefined,
                teams: Array.isArray(orgData.teams) ? orgData.teams.filter((value): value is string => typeof value === "string") : undefined,
                groups: orgData.groups && typeof orgData.groups === "object"
                    ? (orgData.groups as Record<string, string[]>)
                    : undefined,
                members: [],
                ownerCount: ownerSnap.data().count,
                coachCount: coachSnap.data().count,
                athleteCount: athleteSnap.data().count,
                config,
                ownerDocIssue,
            };
        })
    );
};

export const fetchOrganisationSubcollection = async (
    orgId: string,
    name: OrganisationSubcollectionName
): Promise<OrganisationSubcollectionEntry[]> => {
    const snap = await getDocs(collection(db, "organisations", orgId, name));
    return snap.docs.map((entryDoc) => {
        const data = entryDoc.data() as Record<string, unknown>;
        return {
            id: entryDoc.id,
            ...data,
            joinedAt: toDate(data.joinedAt),
        };
    });
};

export const fetchOrganisationMemberNotes = async (
    orgId: string,
    memberId: string
): Promise<OrganisationMemberNote[]> => {
    const snap = await getDocs(collection(db, "organisations", orgId, "members", memberId, "notes"));
    return snap.docs.map((noteDoc) => {
        const data = noteDoc.data() as Record<string, unknown>;
        return {
            id: noteDoc.id,
            memberId,
            content: typeof data.content === "string" ? data.content : "",
            createdBy: typeof data.createdBy === "string" ? data.createdBy : undefined,
            createdAt: toDate(data.createdAt),
            updatedAt: toDate(data.updatedAt),
        };
    });
};

export const updateOrganisationTeams = async (orgId: string, teams: string[]): Promise<void> => {
    await updateDoc(doc(db, "organisations", orgId), { teams });
};

export const updateOrganisationGroups = async (orgId: string, groups: Record<string, string[]>): Promise<void> => {
    await updateDoc(doc(db, "organisations", orgId), { groups });
};

export const updateOrganisationName = async (orgId: string, name: string): Promise<void> => {
    await updateDoc(doc(db, "organisations", orgId), { name });
};

export const updateCoachTeams = async (orgId: string, coachId: string, teams: string[] | null): Promise<void> => {
    await updateDoc(doc(db, "organisations", orgId, "coaches", coachId), { teams });
};

export const updateMedicalTeams = async (orgId: string, medicalId: string, teams: string[] | null): Promise<void> => {
    await updateDoc(doc(db, "organisations", orgId, "medicals", medicalId), { teams });
};

export const updateMemberTeam = async (orgId: string, memberId: string, team: string | null): Promise<void> => {
    await setDoc(doc(db, "organisations", orgId, "members", memberId), { team }, { merge: true });
};

export const updateMemberGroups = async (orgId: string, memberId: string, groups: string[]): Promise<void> => {
    await setDoc(doc(db, "organisations", orgId, "members", memberId), { groups }, { merge: true });
};

export const getOrganisationConfig = async (orgId: string): Promise<OrganisationConfig> => {
    const snap = await getDoc(doc(db, "organisations", orgId, "config", "settings"));
    if (!snap.exists()) return DEFAULT_ORGANISATION_CONFIG;

    const data = snap.data() as Record<string, unknown>;

    return {
        maxAthletes: typeof data.maxAthletes === "number" ? data.maxAthletes : 0,
        maxAthletesForPas: typeof data.maxAthletesForPas === "number" ? data.maxAthletesForPas : 0,
        orgType: data.orgType === "team" || data.orgType === "commercial" ? data.orgType : "individual",
        maxCoaches: typeof data.maxCoaches === "number" ? data.maxCoaches : 0,
        maxTeams: typeof data.maxTeams === "number" ? data.maxTeams : 0,
        billingPeriod: data.billingPeriod === "quarterly" || data.billingPeriod === "annually" ? data.billingPeriod : undefined,
        billingPeriodStart: toDate(data.billingPeriodStart),
        billingPeriodEnd: toDate(data.billingPeriodEnd),
        onboardingRequested: data.onboardingRequested === true,
        athleteSubscription: resolveAthleteSubscription(data.athleteSubscription),
    };
};

export const saveOrganisationConfig = async (orgId: string, config: OrganisationConfig): Promise<void> => {
    // Firestore rejects `undefined` field values, so strip any unset fields before writing
    // rather than enabling ignoreUndefinedProperties.
    const payload: Record<string, unknown> = Object.fromEntries(
        Object.entries(config).filter(([, value]) => value !== undefined)
    );
    await setDoc(doc(db, "organisations", orgId, "config", "settings"), payload, { merge: true });
};
